import React from 'react';
import API from '../../../lib/API/index';
import { useToast } from '../../../hooks/useToast';
import { linkCustomer, LinkCustomerLinkType } from '../helpers';
import rpcShared from '@rockpapercoin/rpc-shared';
import { toSimpleLink } from '../../../lib/helpers';
import { gql } from '@apollo/client';
import { GetProposalPastDueEventPropsEventFragment, UserType } from '#types';
import { Typography } from '@mui/material';
import { FlattenedUser } from '../../../types/user';

export const getProposalPastDueEventPropsEventFragment = gql`
	fragment getProposalPastDueEventPropsEvent on Event {
		category
		resolvedAt
		proposal {
			id
			title
			automatedRemindersEnabled
			sentByUser {
				id
			}
			contact {
				id
				firstName
				lastName
				customer {
					id
					email
					clientUser {
						id
						assignedPlanner {
							id
							organization {
								id
							}
						}
					}
				}
				assignedMember {
					id
				}
				vendor {
					id
					name
					users {
						id
						role
					}
				}
			}
		}
	}
`;

const resendProposal = async function(
	id: string,
	refresh: () => void,
	hookProps: { showError: ( error: unknown ) => void; showSuccess: () => void }
) {
	// there is no bundling with resends
	const response = await API.sendProposal( id, null );

	if ( !( 'errors' in response ) ) {
		hookProps.showSuccess();
		if ( refresh ) {
			return refresh();
		}
	} else {
		response.errors.map( ( error ) => hookProps.showError( error ) );
	}
};

type GetProposalPastDueEventProps = {
	defaultEventProps: {
		event: GetProposalPastDueEventPropsEventFragment;
		message: React.JSX.Element;
		links: {
			type: string;
			text: string;
			link: any;
			onClick?: ( () => any ) | null;
		}[];
		icon: string;
		className: string;
	};
	user: FlattenedUser;
	refresh: () => void;
	openModal: ( onGotItOverride?: () => void ) => void;
} & ReturnType<typeof useToast>;

export const getProposalPastDueEventProps = ( {
	defaultEventProps,
	user,
	refresh,
	showError,
	showSuccess,
	openModal,
}: GetProposalPastDueEventProps ) => {
	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;
	const updatedEventProps = { ...defaultEventProps };
	const proposalLinkProps = {
		text: updatedEventProps.event.proposal?.title,
		link: {
			as: `/proposal/${ updatedEventProps.event.proposal?.id }`,
			href: '/proposal/[id]',
		},
	};
	const proposalLink = toSimpleLink( proposalLinkProps );
	const customerLink = updatedEventProps.event.proposal
		? linkCustomer(
			{ contact: updatedEventProps.event.proposal.contact },
			LinkCustomerLinkType.Contact
		  )
		: undefined;
	const customerNameOrLink = updatedEventProps.event.proposal?.contact
		? updatedEventProps.event.proposal?.contact?.vendor.id ===
				orgUser?.organization.id ||
		  ( orgUser &&
				rpcShared.contacts.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
					updatedEventProps.event.proposal.contact,
					orgUser
				) )
			? customerLink
			: rpcShared.contacts.getCustomerName(
				updatedEventProps.event.proposal.contact
			  ).fullName
		: undefined;

	const vendorLink = toSimpleLink( {
		text: updatedEventProps.event.proposal?.contact?.vendor.name,
		link: {
			href: `/org/${ updatedEventProps.event.proposal?.contact?.vendor.id }`,
		},
	} );
	const vendorNameOrLink =
		updatedEventProps.event.proposal?.contact?.vendor.id ===
		orgUser?.organization.id
			? vendorLink
			: updatedEventProps.event.proposal?.contact?.vendor.name;
	updatedEventProps.icon = 'proposal';
	updatedEventProps.links = [
		{
			type: 'secondary',
			text: 'View',
			link: proposalLinkProps.link,
		},
	];

	const getResendOrViewLink = () => {
		return {
			type: updatedEventProps.event.resolvedAt ? 'secondary' : 'primary',
			text: updatedEventProps.event.resolvedAt ? 'View' : 'Resend',
			link: updatedEventProps.event.resolvedAt ? proposalLink : null,
			onClick: updatedEventProps.event.resolvedAt
				? null
				: () => {
					const proposalId = updatedEventProps.event.proposal?.id;
					if ( !proposalId ) {
						return;
					}
					const performResend = () =>
						resendProposal( proposalId, refresh, {
							showError,
							showSuccess,
						} );
					if (
						updatedEventProps.event.proposal?.automatedRemindersEnabled ===
							false
					) {
						openModal( performResend );
						return;
					}
					performResend();
				  },
		};
	};

	if ( !user?.isLoggedIn || !updatedEventProps.event.proposal?.contact ) {
		// when singing out the user data we need will not be supplied, so do nothing
		return null;
	} else if (
		rpcShared.contacts.isCustomer(
			updatedEventProps.event.proposal.contact,
			user.user.id
		)
	) {
		updatedEventProps.message = (
			<Typography>
				{ proposalLink } from { vendorNameOrLink } is past due.
			</Typography>
		);
	} else if (
		user.userType === UserType.OrgUser &&
		( rpcShared.contacts.isAdminOrManagerOfVendor(
			updatedEventProps.event.proposal.contact,
			user
		) ||
			rpcShared.contacts.isAssignedMember(
				updatedEventProps.event.proposal.contact,
				user
			) )
	) {
		updatedEventProps.message = (
			<Typography>{ proposalLink } is past due. Resend?</Typography>
		);
		updatedEventProps.links = [ getResendOrViewLink() ];
	} else {
		updatedEventProps.message = (
			<Typography className='p--secondary'>
				A proposal from { vendorNameOrLink } that was sent to { customerNameOrLink }{ ' ' }
				is past due: { proposalLink }.
			</Typography>
		);
	}

	return updatedEventProps;
};
